import { SbBlokData, storyblokEditable } from '@storyblok/react/rsc'
import { FC } from 'react'

import { stylePanelSBData } from '@/lib/stylePanel'

import { PhnxChart } from './Chart'

interface DatasetOld {
  type: 'bar' | 'line'
  label: string
  data: string
  colorStyle?: string
  customColors?: string
  datalabelColor?: string
}

interface DatasetNew {
  type: 'bar' | 'line'
  label: string
  data: number[]
  borderColor: string | string[] // Updated to allow arrays
  backgroundColor: string | string[] // Updated to allow arrays
  borderWidth: number
  datalabelColor?: string
}

interface ChartBlokProps {
  blok: {
    title?: string
    variant?: 'vertical' | 'horizontal' | 'horizontalstacked' | 'verticalGraph'
    labels?: string
    yLabels?: string
    xScaleLabel?: string
    yScaleLabel?: string
    yScaleType?: 'number' | 'percent'
    xScaleType?: 'number' | 'percent'
    datasets?: DatasetOld[]
    padding?: SbBlokData[]
    border?: boolean
  } & SbBlokData
}

const mapDataset = (datasets: DatasetOld[] = []): DatasetNew[] => {
  return datasets.map((ds) => {
    const dataArray = ds.data
      .split(',')
      .map((num) => parseFloat(num.trim()))
      .filter((num) => !isNaN(num))

    let borderColor: string | string[] = ''
    let backgroundColor: string | string[] = ''
    let borderWidth = ds.type === 'bar' ? 1 : 3

    // 1. Process Custom Colors
    // We check if it exists AND has length after trimming
    const hasCustomColors = ds.customColors && ds.customColors.trim().length > 0

    if (hasCustomColors) {
      const colorArray = ds.customColors!.split(',').map((c) => {
        const color = c.trim()
        // Fallback: If user forgot the '#', add it (for 3 or 6 char hex codes)
        if (/^[0-9A-F]{3,6}$/i.test(color)) {
          return `#${color}`
        }
        return color
      })

      // If they provided only one color, use the string.
      // If multiple, use the array.
      backgroundColor = colorArray.length === 1 ? colorArray[0] : colorArray
      borderColor = colorArray.length === 1 ? colorArray[0] : colorArray
    } else {
      // 2. Predefined switch (Fallback)
      switch (ds.colorStyle) {
        case 'blue':
          borderColor = '#4D59FF'
          backgroundColor = '#4D59FF'
          break
        case 'purple':
          borderColor = '#C25CFF'
          backgroundColor = '#C25CFF'
          break
        case 'pink':
          borderColor = '#e6cafb'
          backgroundColor = '#e6cafb'
          break
        default:
          borderColor = '#57636d' // Changed from #000 to grey as a safer default
          backgroundColor = '#57636d'
      }
    }

    return {
      type: ds.type,
      label: ds.label,
      data: dataArray,
      borderColor,
      backgroundColor,
      borderWidth,
      datalabelColor: ds.datalabelColor,
    }
  })
}

const Chart: FC<ChartBlokProps> = ({ blok }) => {
  let stylePanel = stylePanelSBData(null, null, null, blok.padding)

  const labelArray =
    blok.labels?.split(',').map((label) => {
      const trimmed = label.trim()
      // If you want to manually control breaks, tell users to use a | in Storyblok
      // e.g., "Long Label | Second Line"
      if (trimmed.includes('|')) {
        return trimmed.split('|').map((s) => s.trim())
      }

      // OR: Automatic wrap (example: split if more than 10 characters)
      if (trimmed.length > 10 && trimmed.includes(' ')) {
        return trimmed.split(' ')
      }

      return trimmed
    }) || []

  const ylabelArray = blok.yLabels?.split(',').map((label) => label.trim()) || []
  const datasets = mapDataset(blok.datasets || [])
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
  const isTablet = typeof window !== 'undefined' && window.innerWidth <= 1250

  return (
    <div
      {...storyblokEditable(blok)}
      className={`container`}
      style={{
        display: 'Flex',
        width: '100%',
        justifyContent: 'center',
        marginLeft: isMobile || isTablet ? '0px' : 'auto',
        marginRight: isMobile || isTablet ? '0px' : 'auto',
        marginBottom: isMobile || isTablet ? '20px' : '60px',
      }}
    >
      <PhnxChart
        title={blok.title || ''}
        variant={blok.variant || 'vertical'}
        labels={labelArray}
        yLabels={ylabelArray}
        xScaleLabel={blok.xScaleLabel || ''}
        xScaleType={blok.xScaleType || 'number'}
        yScaleType={blok.yScaleType || 'number'}
        yScaleLabel={blok.yScaleLabel || ''}
        datasets={datasets}
        padding={stylePanel.padding}
        border={blok.border}
      />
    </div>
  )
}

export default Chart
